'use client';
import React, { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RoleType } from 'types/roles';

import { AuthFormWrapper } from '@components/AuthForm';
import { Button } from '@components/Button';
import { StyledLink } from '@components/Common';
import { Input } from '@components/Input';
import { LoginWrapper } from '@components/Login';
import { SuccessMessage } from '@components/SuccessMessage';
import { TextGroup } from '@components/TextGroups';

import { forgotPassword } from '@utils/supabase/auth-actions';

export function ForgotPassword({ role }: { role: RoleType }) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { t } = useTranslation('common');
  const formRef = useRef<HTMLFormElement>(null);
  const hasError = !!error;

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const email = (formData.get('email') as string) || '';

      try {
        const message = await forgotPassword(email, role);
        setSuccess(message);
      } catch (error: unknown) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(String(error));
        }
      }
    }
  };

  if (success) {
    return (
      <SuccessMessage
        header={t('resetPassword.changePassword')}
        paragraph={t('resetPassword.changePasswordSuccess')}
        illustrationSrc={'/assets/images/Illustration-verify.svg'}
        link={`/${role}/login`}
        buttonLabel={t('login.title')}
      />
    );
  }

  return (
    <LoginWrapper>
      <TextGroup
        header={t('resetPassword.changePassword')}
        paragraph={t('resetPassword.description')}
      />
      <AuthFormWrapper onSubmit={handleFormSubmit} ref={formRef}>
        <Input
          type="email"
          id="email"
          placeholder={t('enterEmail')}
          name="email"
          label={t('email')}
          hasError={hasError}
          required
        />
        <Button type="submit" label={t('resetPassword.request')} variant={'primary'} />
      </AuthFormWrapper>

      <StyledLink href={`/${role}/login`} passHref className="defaultAStyle">
        {t('resetPassword.toLogin')}
      </StyledLink>
    </LoginWrapper>
  );
}
